import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const IncomingCallModal = ({ 
  isOpen, 
  callerName, 
  productName, 
  storeName,
  callId,
  chatId,
  productId,
  storeId,
  onAccept, 
  onReject 
}) => {
  const navigate = useNavigate();
  const audioRef = useRef(null);

  // Helper function to stop all audio
  const stopAllAudio = () => {
    if (audioRef.current) {
      // Clear interval first
      if (audioRef.current.interval) {
        clearInterval(audioRef.current.interval);
      }
      // Close audio context
      if (audioRef.current.audioContext) {
        try {
          audioRef.current.audioContext.close();
        } catch (e) {
          console.warn('Error closing audio context:', e);
        }
      }
      // Clear the ref
      audioRef.current = null;
    }
  };

  useEffect(() => {
    if (isOpen) {
      // Play incoming call sound
      try {
        // Stop any existing audio first
        stopAllAudio();
        
        // Create audio context for ringing sound
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = 800;
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
        
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.5);
        
        // Repeat every 2 seconds
        const interval = setInterval(() => {
          // Check if still open before playing
          if (!isOpen) {
            clearInterval(interval);
            return;
          }
          
          try {
            const osc = audioContext.createOscillator();
            const gain = audioContext.createGain();
            
            osc.connect(gain);
            gain.connect(audioContext.destination);
            
            osc.frequency.value = 800;
            osc.type = 'sine';
            
            gain.gain.setValueAtTime(0.3, audioContext.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
            
            osc.start(audioContext.currentTime);
            osc.stop(audioContext.currentTime + 0.5);
          } catch (e) {
            console.warn('Error playing ring tone:', e);
            clearInterval(interval);
          }
        }, 2000);
        
        audioRef.current = { audioContext, interval };
        
        return () => {
          stopAllAudio();
        };
      } catch (error) {
        console.warn('Could not play incoming call sound:', error);
      }
    } else {
      // Stop sound when modal closes
      stopAllAudio();
    }
  }, [isOpen]);

  const handleAccept = () => {
    // Stop sound immediately
    stopAllAudio();
    
    // Navigate to video call
    if (chatId) {
      navigate(`/marketplace/video-call/${chatId}`);
    } else if (productId && storeId) {
      navigate(`/marketplace/video-call/product/${productId}/store/${storeId}`);
    }
    
    if (onAccept) {
      onAccept();
    }
  };

  const handleReject = () => {
    // Stop sound immediately
    stopAllAudio();
    
    if (onReject) {
      onReject();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 text-center animate-pulse">
        <div className="mb-6">
          <div className="w-24 h-24 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
            <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">📞 Video Call เข้ามา</h3>
          <p className="text-lg text-gray-700 mb-1">{callerName}</p>
          {productName && (
            <p className="text-sm text-gray-500">เกี่ยวกับ: {productName}</p>
          )}
          {storeName && !productName && (
            <p className="text-sm text-gray-500">จากร้าน: {storeName}</p>
          )}
        </div>
        
        <div className="flex gap-4 justify-center">
          <button
            onClick={handleReject}
            className="px-6 py-3 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors flex items-center gap-2"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span>ปฏิเสธ</span>
          </button>
          
          <button
            onClick={handleAccept}
            className="px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors flex items-center gap-2"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            <span>รับสาย</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default IncomingCallModal;


